import { useEffect, useRef, useState } from "react";
import ChevronDownIcon from "@heroicons/react/20/solid/ChevronDownIcon";
import { createPortal } from "react-dom";

import { BreakpointProvider } from "@hsl/core/hooks/useBreakpoint";
import cx from "@hsl/core/utils/cx";
import { type PMCFundMeta } from "@hsl/fund-explorer/metaSchemas";
import { ClientOnly } from "@hsl/lgim-fund-page/src/components/ClientOnly";

import CardColCell from "../components/CardColCell";
import CardModeSelector from "../components/CardModeSelector";
import ContactUsCTA from "../components/ContactUsCTA";
import FundsList from "../components/FundsList";
import GroupBySelector from "../components/GroupBySelector";
import Loading from "../components/Loading";
import PageContainer from "../components/PageContainer";
import PromoCard from "../components/PromoCard";
import SearchBar from "../components/SearchBar";
import { FiltersSection } from "../components/SidebarSections";
import { RetrieveFunds } from "../components/Subscription/RetrieveFunds";
import { SubscriptionSelectionBasket } from "../components/Subscription/SubscriptionSelectionBasket";
import SubscriptionStickyBanner from "../components/Subscription/SubscriptionStickyBanner";
import TailwindIndicator from "../components/TailwindIndicator";
import { SidebarProvider } from "../hooks/useSidebarToggle";
import useExplorerStore from "../store/store";

const FundExplorerView = () => {
    const mainContentRef = useRef<HTMLDivElement>(null);
    const productType = useExplorerStore((state) => state.config.productType);
    const setSort = useExplorerStore((state) => state.setSort);
    const loading = useExplorerStore((state) => state.loading);
    const config = useExplorerStore((state) => state.config);
    const applyFilter = useExplorerStore((state) => state.applyFilter);
    const {
        audienceId,
        routeId,
        promo,
        subscriptionsBannerPartId,
        languageId,
    } = config;
    const version =
        new URL(window.location.href).searchParams.get("version") ?? undefined;
    const availablePromos = promo?.filter((p) =>
        p.audiences.includes(audienceId),
    );
    const [{ partId: promoPartId }] =
        availablePromos && availablePromos.length > 0
            ? availablePromos
            : [{ partId: undefined }];
    const tabContainerElem = document.querySelector(
        ".fund-centre-tabs-container",
    );
    const [tabContainerRight, setTabContainerRight] = useState(
        tabContainerElem?.getBoundingClientRect().right ?? 0,
    );
    const [mainContentWidth, setMainContentWidth] = useState(
        mainContentRef.current?.clientWidth ?? 0,
    );
    useEffect(() => {
        const interval = setInterval(() => {
            setTabContainerRight(
                tabContainerElem?.getBoundingClientRect().right ?? 0,
            );
            setMainContentWidth(mainContentRef.current?.clientWidth ?? 0);
        }, 300);
        return () => clearInterval(interval);
    }, []);
    return (
        <BreakpointProvider>
            <SidebarProvider>
                <ContactUsCTA />
                <div className="page-container-wrap">
                    <PageContainer
                        ref={mainContentRef}
                        sidebarContent={<FiltersSection />}
                        sidebarClassName="w-72"
                        contentContainerClassName="px-2"
                        mainContent={
                            <>
                                <ClientOnly>
                                    <SearchBar />
                                </ClientOnly>
                                {promoPartId && (
                                    <PromoCard
                                        audienceId={audienceId}
                                        partId={promoPartId}
                                        languageId={languageId}
                                        version={version}
                                        onFilter={applyFilter}
                                        style={{
                                            maxWidth: `${mainContentWidth}px`,
                                        }}
                                    />
                                )}
                                <div className="hidden justify-between px-4 pb-1.5 pt-3 sm:flex">
                                    {productType === "pmc" ? (
                                        <>
                                            <div className="flex items-center gap-x-8 pl-4">
                                                <button
                                                    className="ml-0.5 flex items-center gap-1"
                                                    onClick={() =>
                                                        setSort(null)
                                                    }
                                                >
                                                    <span className="inline-block text-left text-sm font-medium">
                                                        Fund
                                                    </span>
                                                </button>
                                                <GroupBySelector />
                                            </div>
                                            <div className="flex items-center gap-x-2 text-left font-medium">
                                                <CardColCell
                                                    className="items-center gap-1 md:inline-flex"
                                                    onClick={() =>
                                                        setSort(
                                                            "capabilityStyle",
                                                        )
                                                    }
                                                >
                                                    Style/Capability
                                                    <SortAwareChevron sortKey="capabilityStyle" />
                                                </CardColCell>
                                                <CardColCell
                                                    className="items-center gap-1 md:inline-flex"
                                                    onClick={() =>
                                                        setSort("subGrouping")
                                                    }
                                                >
                                                    Sub-grouping
                                                    <SortAwareChevron sortKey="subGrouping" />
                                                </CardColCell>
                                            </div>
                                        </>
                                    ) : (
                                        <GroupBySelector />
                                    )}
                                </div>
                                <CardModeSelector className="ml-auto mt-2 w-max sm:hidden" />
                                {loading ? <Loading /> : <FundsList />}
                            </>
                        }
                    />
                </div>
                <ClientOnly>
                    {subscriptionsBannerPartId &&
                        tabContainerElem &&
                        createPortal(
                            <SubscriptionStickyBanner
                                audienceId={audienceId}
                                partId={subscriptionsBannerPartId}
                                languageId={languageId}
                                version={version}
                                style={{
                                    // left: `${tabContainerRight}px`,
                                    right: "50px",
                                }}
                                className="hidden lg:block"
                            />,
                            tabContainerElem,
                        )}
                    <TailwindIndicator />
                    <SubscriptionSelectionBasket />
                    <RetrieveFunds />
                </ClientOnly>
            </SidebarProvider>
        </BreakpointProvider>
    );
};

export default FundExplorerView;

interface Props {
    sortKey: keyof PMCFundMeta;
}

const SortAwareChevron = ({ sortKey }: Props) => {
    const sortBy = useExplorerStore((state) => state.sortBy);
    const sortDirection = useExplorerStore((state) => state.sortDirection);
    return (
        <ChevronDownIcon
            className={cx(
                "h-4 w-4 transition",
                sortBy === sortKey
                    ? [
                          "text-charcoal",
                          sortDirection === "asc" && "rotate-180 transform",
                      ]
                    : "text-taupe-grey",
            )}
        />
    );
};
